import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { CIRCUIT_BUILDER_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Schematic-style circuit builder.
 *
 * Tap two terminals to draw a wire between them. Tap an existing wire to remove it.
 * Tap a switch to toggle it. The engine simulates the circuit by treating wires
 * and components as a graph and BFSing connectivity from the battery's terminals.
 * The LED only lights when:
 *   - bat.pos reaches led.anode (without traversing the LED itself),
 *   - led.cathode reaches bat.neg,
 *   - the path includes the required resistor (no short),
 *   - any required switch is closed.
 *
 * Content shape:
 * {
 *   instructions?: string,
 *   scene?: { viewBox?: string, background?: string },
 *   components: Array<{
 *     id: string,
 *     kind: 'battery' | 'switch' | 'resistor' | 'led',
 *     x: number, y: number,
 *     label?: string,
 *     color?: string,           // led only
 *     closed?: boolean,         // switch only — initial state
 *   }>,
 *   wires?: Array<[string, string]>,           // pre-existing wires (terminalIds)
 *   goal: {
 *     type: 'light_led',
 *     ledId: string,
 *     batteryId: string,
 *     requireSwitchClosed?: string,            // switchId
 *     requireResistorInPath?: string,          // resistorId
 *   },
 *   victory?: { message?: string },
 *   revealWires?: Array<[string, string] | { 0: string, 1: string }>, // optional: used by lesson footer “Show answer”
 * }
 *
 * Guided lesson shell (`onRegisterLessonFooterActions` + `goal`): **Check** in the lesson footer; LED glow and
 * coaching feedback stay hidden until Check. Auto-complete on glow is disabled in that mode. The tap-first
 * **terminal chip** row is omitted in the shell — wire by tapping two gold terminals on the schematic only.
 *
 * Terminal ids are `<componentId>.<key>`:
 *   battery → pos | neg     (top | bottom)
 *   switch  → a   | b       (left | right)
 *   resistor→ a   | b       (left | right)
 *   led     → anode | cathode (top | bottom)
 */

const SCENE_DEFAULT_VIEWBOX = '0 0 340 220';

const COLORS = {
  bg: '#0b1020',
  grid: 'rgba(148,163,184,0.15)',
  wire: '#facc15',
  wireOff: '#71717a',
  wireHit: 'rgba(250, 204, 21, 0)',
  metal: '#fbbf24',
  metalDim: '#a78b3a',
  cathode: '#a1a1aa',
  ledOff: '#27272a',
  textHi: '#fde68a',
  textDim: '#94a3b8',
  termFill: '#0b1020',
  termHi: '#facc15',
};

function terminalPos(comp, key) {
  switch (comp.kind) {
    case 'battery':
      return key === 'pos' ? { x: comp.x, y: comp.y - 30 } : { x: comp.x, y: comp.y + 30 };
    case 'switch':
    case 'resistor':
      return key === 'a' ? { x: comp.x - 30, y: comp.y } : { x: comp.x + 30, y: comp.y };
    case 'led':
      return key === 'anode' ? { x: comp.x, y: comp.y - 18 } : { x: comp.x, y: comp.y + 18 };
    default:
      return { x: comp.x, y: comp.y };
  }
}

function terminalKeysFor(kind) {
  switch (kind) {
    case 'battery':
      return ['pos', 'neg'];
    case 'switch':
    case 'resistor':
      return ['a', 'b'];
    case 'led':
      return ['anode', 'cathode'];
    default:
      return [];
  }
}

function tid(componentId, key) {
  return `${componentId}.${key}`;
}

function parseTid(t) {
  const i = t.lastIndexOf('.');
  return [t.slice(0, i), t.slice(i + 1)];
}

function sameWire(w1, w2) {
  return (
    (w1[0] === w2[0] && w1[1] === w2[1]) || (w1[0] === w2[1] && w1[1] === w2[0])
  );
}

function wireKey(w) {
  return w[0] < w[1] ? `${w[0]}|${w[1]}` : `${w[1]}|${w[0]}`;
}

function buildGraph(components, wires, switchStates) {
  const adj = new Map();
  const addEdge = (a, b) => {
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a).add(b);
    adj.get(b).add(a);
  };
  for (const w of wires) addEdge(w[0], w[1]);
  for (const c of components) {
    if (c.kind === 'resistor') {
      addEdge(tid(c.id, 'a'), tid(c.id, 'b'));
    } else if (c.kind === 'switch') {
      if (switchStates[c.id]) addEdge(tid(c.id, 'a'), tid(c.id, 'b'));
    }
    // LED & battery: no internal edges (LED's anode→cathode is the load itself;
    // battery's pos→neg is the source, not a passive connection).
  }
  return adj;
}

function bfs(adj, start, blockedEdge) {
  const visited = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const n = queue.shift();
    const neigh = adj.get(n);
    if (!neigh) continue;
    for (const m of neigh) {
      if (
        blockedEdge &&
        ((blockedEdge[0] === n && blockedEdge[1] === m) ||
          (blockedEdge[0] === m && blockedEdge[1] === n))
      ) {
        continue;
      }
      if (!visited.has(m)) {
        visited.add(m);
        queue.push(m);
      }
    }
  }
  return visited;
}

function evaluateCircuit(content, wires, switchStates) {
  const components = content.components || [];
  const goal = content.goal || {};
  const battery = components.find((c) => c.id === goal.batteryId && c.kind === 'battery');
  const led = components.find((c) => c.id === goal.ledId && c.kind === 'led');
  if (!battery || !led) {
    return { lit: false, status: 'config', coaching: 'This activity is missing its battery or LED.' };
  }
  const posT = tid(battery.id, 'pos');
  const negT = tid(battery.id, 'neg');
  const anodeT = tid(led.id, 'anode');
  const cathodeT = tid(led.id, 'cathode');

  const adj = buildGraph(components, wires, switchStates);
  const fromPos = bfs(adj, posT);
  const fromNeg = bfs(adj, negT);

  const reachesAnode = fromPos.has(anodeT) && fromNeg.has(cathodeT);
  const reachesReversed = fromPos.has(cathodeT) && fromNeg.has(anodeT);

  // Try with all switches forced closed — used to detect "switch is open" coaching.
  const allClosed = {};
  for (const c of components) {
    if (c.kind === 'switch') allClosed[c.id] = true;
  }
  const adjForced = buildGraph(components, wires, allClosed);
  const fromPosForced = bfs(adjForced, posT);
  const fromNegForced = bfs(adjForced, negT);
  const wouldReachAnode = fromPosForced.has(anodeT) && fromNegForced.has(cathodeT);

  if (reachesAnode) {
    if (goal.requireResistorInPath) {
      const r = components.find((c) => c.id === goal.requireResistorInPath && c.kind === 'resistor');
      if (r) {
        const blocked = [tid(r.id, 'a'), tid(r.id, 'b')];
        const fromPosNoR = bfs(adj, posT, blocked);
        if (fromPosNoR.has(anodeT)) {
          return {
            lit: false,
            status: 'short',
            coaching:
              'Short circuit — current is reaching the LED without passing through the resistor. Without a resistor the LED will burn out. Re-route through the resistor.',
          };
        }
      }
    }
    if (goal.requireSwitchClosed && !switchStates[goal.requireSwitchClosed]) {
      // shouldn't normally happen — open switch breaks reachAnode — but guard anyway.
      return {
        lit: false,
        status: 'switch_open',
        coaching: `Close the ${goal.requireSwitchClosed.toUpperCase()} switch to start the flow.`,
      };
    }
    return {
      lit: true,
      status: 'ok',
      coaching: 'LED is glowing — the loop is complete.',
    };
  }

  // Not lit. Find the most useful coaching message.
  if (reachesReversed) {
    return {
      lit: false,
      status: 'reversed',
      coaching:
        "LED is wired backwards. Current must flow into the long leg (anode), not the short leg (cathode). Swap the LED's two wires.",
    };
  }

  if (wouldReachAnode) {
    // Same wires would work if a switch were closed.
    const openSwitch = components.find(
      (c) => c.kind === 'switch' && !switchStates[c.id],
    );
    if (openSwitch) {
      return {
        lit: false,
        status: 'switch_open',
        coaching: `Almost there — close the ${openSwitch.label || openSwitch.id} switch.`,
      };
    }
  }

  // Otherwise: just incomplete.
  if (wires.length === 0) {
    return {
      lit: false,
      status: 'empty',
      coaching: 'Tap two terminals to draw a wire between them. Connect battery + → switch → resistor → LED → battery −.',
    };
  }
  if (fromPos.size <= 1) {
    return {
      lit: false,
      status: 'open',
      coaching: 'Battery + has no outgoing wire yet. Start by wiring battery + to the switch.',
    };
  }
  if (fromNeg.size <= 1) {
    return {
      lit: false,
      status: 'open',
      coaching: 'Battery − has no wire yet. The cathode side of the LED needs to come back to battery −.',
    };
  }
  return {
    lit: false,
    status: 'open',
    coaching: 'Loop is not complete yet — keep wiring from + all the way around to −.',
  };
}

function wirePathD(p1, p2) {
  // Orthogonal L-shape; choose midpoint axis with the longer span.
  const dx = Math.abs(p1.x - p2.x);
  const dy = Math.abs(p1.y - p2.y);
  if (dx >= dy) {
    const mid = (p1.x + p2.x) / 2;
    return `M${p1.x},${p1.y} L${mid},${p1.y} L${mid},${p2.y} L${p2.x},${p2.y}`;
  }
  const mid = (p1.y + p2.y) / 2;
  return `M${p1.x},${p1.y} L${p1.x},${mid} L${p2.x},${mid} L${p2.x},${p2.y}`;
}

function BatteryGlyph({ comp }) {
  return (
    <g transform={`translate(${comp.x},${comp.y})`}>
      {/* + side: long thin plate */}
      <line x1={-14} y1={-12} x2={14} y2={-12} stroke={COLORS.metal} strokeWidth={4} strokeLinecap="round" />
      {/* − side: short thick plate */}
      <line x1={-8} y1={6} x2={8} y2={6} stroke={COLORS.metal} strokeWidth={6} strokeLinecap="round" />
      {/* leads */}
      <line x1={0} y1={-30} x2={0} y2={-12} stroke={COLORS.metal} strokeWidth={2} />
      <line x1={0} y1={6} x2={0} y2={30} stroke={COLORS.metal} strokeWidth={2} />
      <text x={20} y={-10} fill={COLORS.textHi} fontSize={11} fontWeight={700}>
        {comp.label || '9V'}
      </text>
      <text x={20} y={2} fill={COLORS.textDim} fontSize={10}>
        +
      </text>
      <text x={20} y={14} fill={COLORS.textDim} fontSize={10}>
        −
      </text>
    </g>
  );
}

function ResistorGlyph({ comp }) {
  // zigzag from -22 to +22 along y=0
  const d =
    'M -22 0 L -18 -7 L -10 7 L -2 -7 L 6 7 L 14 -7 L 18 7 L 22 0';
  return (
    <g transform={`translate(${comp.x},${comp.y})`}>
      <line x1={-30} y1={0} x2={-22} y2={0} stroke={COLORS.metal} strokeWidth={2} />
      <line x1={22} y1={0} x2={30} y2={0} stroke={COLORS.metal} strokeWidth={2} />
      <path d={d} stroke={COLORS.metal} strokeWidth={2} fill="none" />
      <text x={0} y={20} fill={COLORS.textHi} fontSize={10} fontWeight={700} textAnchor="middle">
        {comp.label || 'R'}
      </text>
    </g>
  );
}

function SwitchGlyph({ comp, closed, onToggle, disabled }) {
  return (
    <g
      transform={`translate(${comp.x},${comp.y})`}
      style={{ cursor: disabled ? 'default' : 'pointer' }}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onToggle?.();
      }}
    >
      {/* leads to terminals */}
      <line x1={-30} y1={0} x2={-22} y2={0} stroke={COLORS.metal} strokeWidth={2} />
      <line x1={22} y1={0} x2={30} y2={0} stroke={COLORS.metal} strokeWidth={2} />
      {/* contact pads */}
      <circle cx={-22} cy={0} r={3} fill={COLORS.metal} />
      <circle cx={22} cy={0} r={3} fill={COLORS.metal} />
      {/* lever */}
      <line
        x1={-22}
        y1={0}
        x2={closed ? 22 : 18}
        y2={closed ? 0 : -18}
        stroke={COLORS.metal}
        strokeWidth={3}
        strokeLinecap="round"
      />
      {/* hit zone for tap */}
      <rect x={-26} y={-22} width={52} height={26} fill="transparent" />
      <text x={0} y={20} fill={COLORS.textHi} fontSize={10} fontWeight={700} textAnchor="middle">
        {comp.label || 'Switch'} {closed ? '(closed)' : '(open)'}
      </text>
    </g>
  );
}

function LedGlyph({ comp, lit }) {
  const color = comp.color || '#ef4444';
  return (
    <g transform={`translate(${comp.x},${comp.y})`}>
      {/* leads */}
      <line x1={0} y1={-18} x2={0} y2={-10} stroke={COLORS.metal} strokeWidth={2} />
      <line x1={0} y1={10} x2={0} y2={18} stroke={COLORS.cathode} strokeWidth={2} />
      {/* triangle (anode at top, point downward) */}
      <polygon
        points="-10,-10 10,-10 0,8"
        fill={lit ? color : COLORS.ledOff}
        stroke={COLORS.metal}
        strokeWidth={1.5}
      />
      {/* cathode bar */}
      <line x1={-12} y1={9} x2={12} y2={9} stroke={COLORS.cathode} strokeWidth={2} />
      {/* glow when lit */}
      {lit ? (
        <>
          <circle cx={0} cy={-1} r={22} fill={color} opacity={0.15}>
            <animate attributeName="r" values="20;24;20" dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.18;0.32;0.18" dur="1.4s" repeatCount="indefinite" />
          </circle>
          {/* emission rays */}
          <g stroke={color} strokeWidth={1.5} strokeLinecap="round" opacity={0.85}>
            <line x1={12} y1={-14} x2={20} y2={-22} />
            <line x1={16} y1={-9} x2={24} y2={-13} />
            <line x1={16} y1={-2} x2={24} y2={-2} />
          </g>
        </>
      ) : null}
      <text x={20} y={-2} fill={COLORS.textHi} fontSize={10} fontWeight={700}>
        {comp.label || 'LED'}
      </text>
      <text x={20} y={10} fill={COLORS.textDim} fontSize={9}>
        anode +
      </text>
      <text x={20} y={20} fill={COLORS.textDim} fontSize={9}>
        cathode −
      </text>
    </g>
  );
}

export default function CircuitBuilderEngine({
  title = 'Build the circuit',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_circuit_builder',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const shellLessonDefer =
    typeof onRegisterLessonFooterActions === 'function' && content?.goal != null;

  const components = content.components || [];
  const initialWires = useMemo(
    () => (content.wires || []).map((w) => [w[0], w[1]]),
    [content.wires],
  );
  const initialSwitches = useMemo(() => {
    const m = {};
    for (const c of components) {
      if (c.kind === 'switch') m[c.id] = !!c.closed;
    }
    return m;
  }, [components]);

  const [wires, setWires] = useState(initialWires);
  const [switchStates, setSwitchStates] = useState(initialSwitches);
  const [selected, setSelected] = useState(null);
  const [evalVisible, setEvalVisible] = useState(() => !shellLessonDefer);
  const [completed, setCompleted] = useState(false);
  const [a11y, setA11y] = useState('');
  const [showCelebration, celebrate] = useCelebration();
  const completedRef = useRef(false);
  const lockSinceRef = useRef(null);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: CIRCUIT_BUILDER_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });

  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer, resetSession } = guided;

  const evalResult = useMemo(
    () => evaluateCircuit(content, wires, switchStates),
    [content, wires, switchStates],
  );

  const showEval = !shellLessonDefer || evalVisible;
  const lit = evalResult.lit;
  const displayLit = lit && (showEval || completed);

  useEffect(() => {
    setEvalVisible(!shellLessonDefer);
  }, [activityId, shellLessonDefer]);

  // Lock-in on lit (demo / no lesson shell only — shell mode completes on footer Check)
  useEffect(() => {
    if (shellLessonDefer) return undefined;
    if (completedRef.current) return undefined;
    if (!evalResult.lit) {
      lockSinceRef.current = null;
      return undefined;
    }
    lockSinceRef.current = performance.now();
    setA11y('Loop complete — LED is glowing.');
    const t = window.setTimeout(() => {
      if (!completedRef.current && lockSinceRef.current) {
        completedRef.current = true;
        setCompleted(true);
        noteCorrectAnswer();
        celebrate();
        trackMiniAppEvent.completed(activityId);
        onComplete?.({
          activityId,
          engine: 'circuit_builder',
          perfect: true,
          wireCount: wires.length,
        });
      }
    }, 700);
    return () => window.clearTimeout(t);
  }, [shellLessonDefer, evalResult.lit, activityId, wires.length, celebrate, onComplete, noteCorrectAnswer]);

  const commitCheck = useCallback(() => {
    if (!shellLessonDefer || completed) return;
    noteUserAction();
    setEvalVisible(true);
    const er = evaluateCircuit(content, wires, switchStates);
    onProgress?.({
      activityId,
      engine: 'circuit_builder',
      phase: 'check',
      lastCorrect: er.lit,
    });
    if (er.lit) {
      if (completedRef.current) return;
      completedRef.current = true;
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('Loop complete — LED is glowing.');
      trackMiniAppEvent.interaction(activityId, 'correct');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({
        activityId,
        engine: 'circuit_builder',
        perfect: true,
        wireCount: wires.length,
      });
    } else {
      noteIncorrectAnswer();
      setA11y('Not quite — adjust wires or the switch, then tap Check again.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    shellLessonDefer,
    completed,
    noteUserAction,
    content,
    wires,
    switchStates,
    activityId,
    onProgress,
    noteCorrectAnswer,
    celebrate,
    onComplete,
    noteIncorrectAnswer,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    const rw = content.revealWires;
    if (Array.isArray(rw) && rw.length) {
      setWires(rw.map((pair) => [String(pair[0]), String(pair[1])]));
    }
    const goal = content.goal || {};
    if (goal.requireSwitchClosed) {
      setSwitchStates((prev) => ({ ...prev, [goal.requireSwitchClosed]: true }));
    }
    setEvalVisible(true);
    setSelected(null);
  }, [completed, noteUserAction, content]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || !shellLessonDefer) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer: commitCheck,
      checkAnswerDisabled: false,
      showAnswer: revealAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    onRegisterLessonFooterActions,
    completed,
    shellLessonDefer,
    commitCheck,
    revealAnswer,
  ]);

  const findWire = useCallback(
    (a, b) => wires.find((w) => sameWire(w, [a, b])),
    [wires],
  );

  const handleTerminalClick = (termId) => {
    if (completed) return;
    noteUserAction();
    if (!selected) {
      setSelected(termId);
      onProgress?.({ activityId, engine: 'circuit_builder', phase: 'select', termId });
      return;
    }
    if (selected === termId) {
      setSelected(null);
      if (shellLessonDefer) setEvalVisible(false);
      return;
    }
    // Reject same-component terminal pairs (no point wiring a battery to itself).
    const [c1] = parseTid(selected);
    const [c2] = parseTid(termId);
    if (c1 === c2) {
      setSelected(termId);
      if (shellLessonDefer) setEvalVisible(false);
      return;
    }
    setWires((prev) => {
      const existing = prev.find((w) => sameWire(w, [selected, termId]));
      if (existing) return prev.filter((w) => !sameWire(w, [selected, termId]));
      return [...prev, [selected, termId]];
    });
    onProgress?.({
      activityId,
      engine: 'circuit_builder',
      phase: 'wire',
      from: selected,
      to: termId,
    });
    setSelected(null);
    if (shellLessonDefer) setEvalVisible(false);
  };

  const handleWireClick = (w) => {
    if (completed) return;
    noteUserAction();
    setWires((prev) => prev.filter((x) => !sameWire(x, w)));
    setSelected(null);
    if (shellLessonDefer) setEvalVisible(false);
  };

  const handleToggleSwitch = (id) => {
    if (completed) return;
    noteUserAction();
    setSwitchStates((s) => ({ ...s, [id]: !s[id] }));
    onProgress?.({ activityId, engine: 'circuit_builder', phase: 'switch', switchId: id });
    if (shellLessonDefer) setEvalVisible(false);
  };

  const resetWires = () => {
    if (completed) return;
    resetSession();
    setWires(initialWires);
    setSwitchStates(initialSwitches);
    setSelected(null);
    onProgress?.({ activityId, engine: 'circuit_builder', phase: 'reset' });
    if (shellLessonDefer) setEvalVisible(false);
  };

  const viewBox = content.scene?.viewBox || SCENE_DEFAULT_VIEWBOX;
  const bg = content.scene?.background || COLORS.bg;

  const termLookup = useMemo(() => {
    const m = new Map();
    for (const c of components) {
      for (const k of terminalKeysFor(c.kind)) {
        m.set(tid(c.id, k), terminalPos(c, k));
      }
    }
    return m;
  }, [components]);

  // Switches that need a UI toggle below the schematic, in case tapping the lever feels unclear.
  const switchList = components.filter((c) => c.kind === 'switch');

  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const compactCircuit = tapFirst && singleSurface;

  const tapWireChips = useMemo(() => {
    if (!tapFirst || shellLessonDefer || !selected || completed) return [];
    const [ownerId] = parseTid(selected);
    const out = [];
    for (const c of components) {
      if (c.id === ownerId) continue;
      for (const k of terminalKeysFor(c.kind)) {
        const termId = tid(c.id, k);
        const short =
          k === 'pos'
            ? '+'
            : k === 'neg'
              ? '−'
              : k === 'anode'
                ? '+'
                : k === 'cathode'
                  ? '−'
                  : k.toUpperCase();
        out.push({ id: termId, label: `${c.label || c.id} ${short}` });
      }
    }
    return out;
  }, [tapFirst, shellLessonDefer, selected, completed, components]);

  const coachingDisplay = useMemo(() => {
    if (shellLessonDefer && !evalVisible) return '';
    if (compactCircuit && !evalResult.lit && evalResult.status === 'empty' && wires.length === 0) return '';
    return evalResult.coaching;
  }, [
    shellLessonDefer,
    evalVisible,
    compactCircuit,
    evalResult.lit,
    evalResult.status,
    evalResult.coaching,
    wires.length,
  ]);

  /** Reserve coaching strip height on compact phones when feedback is visible. */
  const reserveStatusSlot =
    compactCircuit && !completed && (Boolean(coachingDisplay) || (shellLessonDefer && evalVisible));

  return (
    <div style={{ ...getEngineRootStyle({ singleSurface }), position: 'relative', ...(guided.contentReserveStyle || {}) }}>
      <CelebrationOverlay
        visible={showCelebration}
        message={content.victory?.message || 'The circuit lit up!'}
      />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, ...(compactCircuit ? { margin: '0 0 6px' } : {}) }}>{title}</h2>
      ) : null}
      {!tapFirst && content.instructions && (
        <p style={{ ...instructionsStyle, ...(compactCircuit ? { margin: '0 0 8px', fontSize: 14 } : {}) }}>
          {content.instructions}
        </p>
      )}

      {tapFirst && !completed && !shellLessonDefer ? (
        <div
          role="region"
          aria-label={selected ? 'Choose a terminal to connect' : 'Circuit wiring'}
          style={{
            marginBottom: compactCircuit ? 8 : 10,
            minHeight: compactCircuit ? 158 : 132,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: 8,
          }}
        >
          {selected ? (
            <>
              <span
                style={{
                  width: '100%',
                  fontSize: compactCircuit ? 11 : 12,
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  opacity: 0.85,
                  lineHeight: 1.3,
                }}
              >
                Connect {selected} to:
              </span>
              <div
                role="group"
                aria-label="Tap a terminal to connect"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: compactCircuit ? 6 : 8,
                  alignItems: 'stretch',
                }}
              >
                {tapWireChips.map((ch) => (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => handleTerminalClick(ch.id)}
                    style={{
                      ...secondaryBtn,
                      flex: '1 1 100px',
                      minHeight: compactCircuit ? 44 : 48,
                      fontSize: compactCircuit ? 12 : 13,
                      fontWeight: 700,
                    }}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p
              style={{
                margin: 0,
                fontSize: compactCircuit ? 12 : 13,
                fontWeight: 600,
                color: 'var(--color-text)',
                opacity: 0.55,
                lineHeight: 1.4,
                alignSelf: 'stretch',
              }}
            >
              Tap a gold terminal on the diagram, then tap a matching label here to draw a wire.
            </p>
          )}
        </div>
      ) : null}

      <div
        style={{
          background: bg,
          borderRadius: compactCircuit ? 12 : 16,
          border: '1px solid var(--color-border)',
          marginBottom: compactCircuit ? 8 : 12,
          overflow: 'hidden',
        }}
      >
        <svg
          viewBox={viewBox}
          width="100%"
          style={{ display: 'block', touchAction: 'manipulation' }}
          role="img"
          aria-label="Circuit schematic"
        >
          {/* dot grid */}
          <defs>
            <pattern id="ckt-grid" width={20} height={20} patternUnits="userSpaceOnUse">
              <circle cx={1} cy={1} r={1} fill={COLORS.grid} />
            </pattern>
          </defs>
          <rect x={0} y={0} width="100%" height="100%" fill="url(#ckt-grid)" />

          {/* wires */}
          {wires.map((w) => {
            const p1 = termLookup.get(w[0]);
            const p2 = termLookup.get(w[1]);
            if (!p1 || !p2) return null;
            const k = wireKey(w);
            return (
              <g key={k}>
                {/* wide invisible hit area */}
                <path
                  d={wirePathD(p1, p2)}
                  stroke="transparent"
                  strokeWidth={14}
                  fill="none"
                  style={{ cursor: completed ? 'default' : 'pointer' }}
                  onClick={() => handleWireClick(w)}
                />
                <path
                  d={wirePathD(p1, p2)}
                  stroke={displayLit ? COLORS.wire : COLORS.wireOff}
                  strokeWidth={3}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pointerEvents="none"
                  style={
                    displayLit
                      ? { filter: 'drop-shadow(0 0 4px rgba(250,204,21,0.6))' }
                      : undefined
                  }
                />
              </g>
            );
          })}

          {/* components */}
          {components.map((c) => {
            if (c.kind === 'battery') return <BatteryGlyph key={c.id} comp={c} />;
            if (c.kind === 'resistor') return <ResistorGlyph key={c.id} comp={c} />;
            if (c.kind === 'switch')
              return (
                <SwitchGlyph
                  key={c.id}
                  comp={c}
                  closed={!!switchStates[c.id]}
                  disabled={completed}
                  onToggle={() => handleToggleSwitch(c.id)}
                />
              );
            if (c.kind === 'led')
              return <LedGlyph key={c.id} comp={c} lit={displayLit} />;
            return null;
          })}

          {/* terminals (drawn last so they're on top) */}
          {components.map((c) =>
            terminalKeysFor(c.kind).map((k) => {
              const id = tid(c.id, k);
              const p = terminalPos(c, k);
              const active = selected === id;
              return (
                <g key={id}>
                  {active ? (
                    <circle cx={p.x} cy={p.y} r={10} fill="rgba(250,204,21,0.18)">
                      <animate
                        attributeName="r"
                        values="8;12;8"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  ) : null}
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={6}
                    fill={active ? COLORS.termHi : COLORS.termFill}
                    stroke={active ? COLORS.termHi : COLORS.metal}
                    strokeWidth={2}
                    style={{ cursor: completed ? 'default' : 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTerminalClick(id);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Terminal ${id}`}
                  />
                </g>
              );
            }),
          )}
        </svg>
      </div>

      {reserveStatusSlot ? (
        <div
          aria-live="polite"
          style={{
            marginTop: 0,
            marginBottom: 8,
            minHeight: 132,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          {coachingDisplay ? (
            <div
              style={{
                flex: 1,
                alignSelf: 'stretch',
                ...(displayLit ? feedbackCorrect : feedbackIncorrect),
                padding: '10px 12px',
                fontSize: 13,
                lineHeight: 1.45,
                overflow: 'visible',
                wordBreak: 'break-word',
              }}
            >
              {coachingDisplay}
            </div>
          ) : (
            <div style={{ flex: 1 }} aria-hidden />
          )}
        </div>
      ) : coachingDisplay && !completed ? (
        <div
          aria-live="polite"
          style={{
            ...(displayLit ? feedbackCorrect : feedbackIncorrect),
            ...(compactCircuit
              ? { marginTop: 8, padding: '8px 10px', fontSize: 13, lineHeight: 1.35 }
              : {}),
          }}
        >
          {coachingDisplay}
        </div>
      ) : null}

      {/* switch toggle row */}
      {switchList.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {switchList.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleToggleSwitch(s.id)}
              disabled={completed}
              aria-pressed={!!switchStates[s.id]}
              style={{
                ...secondaryBtn,
                width: 'auto',
                flex: '1 1 auto',
                minHeight: 44,
                padding: '8px 12px',
                background: switchStates[s.id] ? 'rgba(250,204,21,0.18)' : 'var(--color-bg)',
                borderColor: switchStates[s.id] ? COLORS.metal : 'var(--color-border)',
              }}
            >
              {switchStates[s.id] ? '⚡ ' : '○ '}
              {s.label || s.id}: {switchStates[s.id] ? 'closed' : 'open'}
            </button>
          ))}
        </div>
      ) : null}

      {/* utility row */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          onClick={resetWires}
          disabled={completed}
          style={{ ...secondaryBtn, width: 'auto', flex: 1, minHeight: 44 }}
        >
          ↺ Reset wires
        </button>
        <span
          style={{
            ...secondaryBtn,
            width: 'auto',
            flex: 1,
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            background: 'var(--color-bg)',
            fontSize: 14,
          }}
        >
          Wires: {wires.length}
        </span>
      </div>

      {completed ? (
        <div style={{ ...feedbackCorrect, marginTop: 12 }}>
          {content.victory?.message || 'Loop complete — the LED is glowing.'}
        </div>
      ) : null}
    </div>
  );
}
